import React, { useState, useEffect } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    Box,
    Typography,
    Grid,
    Pagination,
    CircularProgress,
    Fade,
    Container,
    Paper,
    InputBase,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    CategoryRounded,
    ArrowBackRounded,
    SearchRounded,
    CloseRounded,
    FilterListRounded,
} from "@mui/icons-material";
import { Footer } from "../../components/Layout/Footer";
import { Header1 } from "../../components/Layout/Header1.js";
import CardProduct from "../../components/Products/CardProduct";
import MethodGet from "../../config/Service";
import FilterPanel from "../Catalogo/FilterPanel";
import SEO from "../../components/SEO/SEO";

const CategoryPage = () => {
    const { categorySlug } = useParams();
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Data states
    const [category, setCategory] = useState(null);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    // Filter states within Category Page
    // We already have "selectedCategory" fixed by the URL, but user might want to filter by Subcategory, Brand, Price
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [priceRange, setPriceRange] = useState([0, 10000]);
    const [debouncedPriceRange, setDebouncedPriceRange] = useState([0, 10000]);
    const [isPriceRangeManuallySet, setIsPriceRangeManuallySet] = useState(false);
    const [sortBy, setSortBy] = useState("default");
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);

    // Products
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [page, setPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(24);

    // Debounce helpers
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedPriceRange(priceRange), 500);
        return () => clearTimeout(timer);
    }, [priceRange]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 600);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Load Initial Data (Categories & Brands)
    useEffect(() => {
        // Categories
        MethodGet("/cate")
            .then((res) => {
                const cats = res.data.categories || [];
                setCategories(cats);

                // Find current category from URL slug
                // Assuming slug is name lowercased and dashed
                const found = cats.find(
                    (c) => c.name.toLowerCase().replace(/\s+/g, "-") === categorySlug
                );

                if (found) {
                    setCategory(found);
                    setSubcategories(found.subcategories || []);
                } else {
                    // Fallback search by ID if slug is numeric? Or handle 404
                    console.warn("Category not found for slug:", categorySlug);
                }
            })
            .catch((err) => console.error(err));

        // Brands
        MethodGet("/distribucion/products/brands")
            .then((res) => setBrands(res.data || []))
            .catch((err) => console.error(err));

    }, [categorySlug]);

    // Load Products
    useEffect(() => {
        if (!category) return;
        setLoading(true);

        const params = new URLSearchParams();
        params.set("page", page);
        params.set("per_page", itemsPerPage);
        params.set("category_id", category.id);

        // Additional Filters
        if (selectedSubcategory) params.set("subcategory_id", selectedSubcategory);
        if (selectedBrand) params.set("brand_id", selectedBrand);
        if (sortBy !== "default") params.set("sort_by", sortBy);
        if (debouncedSearch) params.set("search", debouncedSearch);
        if (isPriceRangeManuallySet) {
            params.set("min_price", debouncedPriceRange[0]);
            params.set("max_price", debouncedPriceRange[1]);
        }

        MethodGet(`/distribucion/products/all?${params.toString()}`)
            .then((res) => {
                setProducts(res.data.data || res.data || []);
                setTotalProducts(
                    res.data.total || (res.data.data ? res.data.data.length : 0)
                );
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    }, [
        category,
        page,
        itemsPerPage,
        sortBy,
        debouncedSearch,
        selectedSubcategory,
        selectedBrand,
        debouncedPriceRange
    ]);

    const handleClearFilters = () => {
        setSelectedSubcategory(null);
        setSelectedBrand(null);
        setIsPriceRangeManuallySet(false);
        setPriceRange([0, 10000]);
        setSortBy("default");
        setSearchTerm("");
        setPage(1);
    };

    if (!category && !loading) return null; // Or 404 component

    return (
        <Box sx={{ backgroundColor: "#fcfcfc", minHeight: "100vh" }}>
            <SEO
                title={category ? `${category.name} - MerryColor` : "Categoría - MerryColor"}
                description={category ? `Descubre nuestra selección exclusiva de ${category.name} en Merrycolor. Productos de belleza y cuidado personal de alta calidad.` : "Explora nuestras categorías de productos."}
                url={`/categoria/${categorySlug}`}
            />

            <Header1 />

            {/* Hero Section - Soft Gradient */}
            <Box
                sx={{
                    background: "linear-gradient(135deg, #fff0f5 0%, #fff 100%)",
                    pt: { xs: 4, md: 6 },
                    pb: { xs: 6, md: 8 },
                    px: 2,
                    position: "relative",
                    overflow: "hidden",
                    borderBottom: "1px solid #ffeef5"
                }}
            >
                <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
                    <Link
                        to="/catalogo"
                        style={{
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            color: "#801F56",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            marginBottom: 24,
                        }}
                    >
                        <ArrowBackRounded sx={{ fontSize: 18 }} />
                        Volver al catálogo
                    </Link>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <Box
                            sx={{
                                width: { xs: 80, md: 100 },
                                height: { xs: 80, md: 100 },
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #801F56, #e94560)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 10px 25px rgba(128,31,86,0.2)",
                                color: "#fff"
                            }}
                        >
                            <CategoryRounded sx={{ fontSize: { xs: 40, md: 50 } }} />
                        </Box>

                        <Box>
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: "2rem", md: "3rem" },
                                    color: "#111",
                                    lineHeight: 1.2,
                                    mb: 0.5
                                }}
                            >
                                {category?.name || "Cargando..."}
                            </Typography>
                            <Typography sx={{ color: "#666", fontSize: "1.1rem" }}>
                                Explora los mejores productos de {category?.name?.toLowerCase()}
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Main Layout */}
            <Container maxWidth="xl" sx={{ mt: -4, position: "relative", zIndex: 10, pb: 8 }}>
                <Grid container spacing={4}>
                    {/* Sidebar Filters */}
                    <Grid item xs={12} md={3}>
                        <Box sx={{ position: "sticky", top: 120 }}>
                            <FilterPanel
                                // Categories - Fixed to current (or allow navigation?)
                                // If we want to allow switching categories, standard behavior is navigation.
                                // If user selects another category, we navigate.
                                categories={categories}
                                selectedCategory={category?.id}
                                onCategoryChange={(val) => {
                                    if (!val) {
                                        history.push({
                                            pathname: '/catalogo',
                                            state: { scrollToSearch: true }
                                        });
                                    } else {
                                        const c = categories.find(cat => cat.id === val);
                                        if (c) history.push(`/categoria/${c.name.toLowerCase().replace(/\s+/g, '-')}`);
                                    }
                                }}

                                subcategories={subcategories}
                                selectedSubcategory={selectedSubcategory}
                                onSubcategoryChange={(val) => { setSelectedSubcategory(val); setPage(1); }}

                                // Brand Navigation
                                brands={brands}
                                selectedBrand={selectedBrand}
                                onBrandChange={(val) => { setSelectedBrand(val); setPage(1); }}

                                // Price
                                minPrice={0}
                                maxPrice={10000}
                                priceRange={priceRange}
                                onPriceChange={(val, manual) => { setPriceRange(val); if (manual) setIsPriceRangeManuallySet(true); }}

                                // Sort
                                sortBy={sortBy}
                                onSortChange={setSortBy}

                                // Clear
                                onClearFilters={handleClearFilters}

                                // Mobile
                                isMobile={isMobile}
                                isOpen={filterPanelOpen}
                                onToggle={() => setFilterPanelOpen(!filterPanelOpen)}
                            />
                        </Box>
                    </Grid>

                    {/* Products Column */}
                    <Grid item xs={12} md={9}>
                        {/* Search & Mobile Filter Toggle */}
                        <Box sx={{ mb: 4, display: "flex", gap: 2 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: "#fff",
                                    borderRadius: "12px",
                                    px: 2,
                                    py: 1,
                                    border: "1px solid #eee",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                                    transition: "all 0.2s",
                                    "&:focus-within": {
                                        borderColor: "#801F56",
                                        boxShadow: "0 4px 12px rgba(128,31,86,0.1)",
                                    }
                                }}
                            >
                                <SearchRounded sx={{ color: "#aaa", mr: 1.5 }} />
                                <InputBase
                                    placeholder={`Buscar en ${category?.name || "esta categoría"}...`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{ flex: 1, fontSize: "1rem" }}
                                />
                                {searchTerm && (
                                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                                        <CloseRounded fontSize="small" />
                                    </IconButton>
                                )}
                            </Paper>

                            {isMobile && (
                                <IconButton
                                    onClick={() => setFilterPanelOpen(true)}
                                    sx={{
                                        backgroundColor: "#111",
                                        color: "#fff",
                                        borderRadius: "12px",
                                        width: 56,
                                        '&:hover': { backgroundColor: "#333" }
                                    }}
                                >
                                    <FilterListRounded />
                                </IconButton>
                            )}
                        </Box>

                        {/* Grid */}
                        {loading ? (
                            <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
                                <CircularProgress sx={{ color: "#801F56" }} />
                            </Box>
                        ) : products.length > 0 ? (
                            <Fade in={true}>
                                <Box>
                                    <Grid container spacing={3}>
                                        {products.map((product) => (
                                            <Grid item xs={6} md={4} lg={4} key={product.id}>
                                                <CardProduct product={product} />
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {totalProducts > itemsPerPage && (
                                        <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
                                            <Pagination
                                                count={Math.ceil(totalProducts / itemsPerPage)}
                                                page={page}
                                                onChange={(e, v) => { setPage(v); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                                                color="primary"
                                                size="large"
                                                sx={{
                                                    '& .Mui-selected': {
                                                        backgroundColor: '#801F56 !important',
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Fade>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                                <CategoryRounded sx={{ fontSize: 60, color: '#eee' }} />
                                <Typography variant="h6" color="textSecondary">No se encontraron productos en esta categoría.</Typography>
                                <Typography variant="body2" color="textSecondary">Intenta ajustar los filtros o buscar otro término.</Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Container>

            <Footer />
        </Box>
    );
};

export default CategoryPage;
